import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { SlidersHorizontal, X, ChevronDown, ChevronRight, LayoutGrid } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import TopHeader from '../components/layout/TopHeader.jsx'
import { ProductCard, ProductCardSkeleton, getCategoryEmoji } from '../components/ui/index.jsx'
import { productService, categoryService } from '../services/api.js'
import { useAppStore, useAuthStore } from '../store/index.js'

const SORT_OPTIONS = [
  { label: 'Recommended',        value: 'newest' },
  { label: 'Price: Low–High',    value: 'price_asc' },
  { label: 'Price: High–Low',    value: 'price_desc' },
  { label: 'Top Rated',          value: 'rating' },
]

const UOM_FILTERS = [
  { label: 'Single',   emoji: '1️⃣',  value: 'single',  test: (ps)           => ps === 1,  keg: false },
  { label: '4-Pack',   emoji: '📦',  value: '4pack',   test: (ps)           => ps === 4,  keg: false },
  { label: '6-Pack',   emoji: '📦',  value: '6pack',   test: (ps)           => ps === 6,  keg: false },
  { label: '12-Pack',  emoji: '📦',  value: '12pack',  test: (ps)           => ps === 12, keg: false },
  { label: 'Case',     emoji: '🗃️', value: 'case',    test: (ps)           => ps >= 20,  keg: false },
  { label: 'Keg',      emoji: '🪣',  value: 'keg',     test: ()             => true,      keg: true  },
]

const PAGE_SIZE = 36

// ── Category grouping definition ───────────────────────────────────────────────
// Each group has a label, emoji, and a list of lowercase substrings to match category names
const CAT_GROUPS = [
  {
    key: 'spirits',
    label: 'Spirits',
    emoji: '🥃',
    color: '#8B4513',
    match: ['whisky', 'whiskey', 'bourbon', 'scotch', 'vodka', 'gin', 'rum', 'tequila',
            'brandy', 'cognac', 'cane', 'shooters', 'liqueur', 'aperitif', 'other spirits',
            'spirits', 'non alcoholic spirits'],
  },
  {
    key: 'wine',
    label: 'Wine & Sparkling',
    emoji: '🍷',
    color: '#722F37',
    match: ['wine', 'champagne', 'sparkling', 'port', 'sherry', 'muscadel', 'box wine'],
  },
  {
    key: 'beer',
    label: 'Beer & Cider',
    emoji: '🍺',
    color: '#DAA520',
    match: ['beer', 'cider'],
  },
  {
    key: 'rtd',
    label: 'RTDs & Cocktails',
    emoji: '🍹',
    color: '#0e947a',
    match: ['rtd', 'r t d', 'cocktail'],
  },
  {
    key: 'softdrinks',
    label: 'Non-Alcoholic',
    emoji: '💧',
    color: '#4169E1',
    match: ['soft drink', 'mineral', 'water', 'juice', 'energy', 'cordial', 'non alcoholic'],
  },
  {
    key: 'other',
    label: 'Tobacco & Other',
    emoji: '🎁',
    color: '#666',
    match: ['tobacco', 'cigarette', 'glassware', 'snack', 'gift'],
  },
]

// Assign a category to a group key
function getGroupKey(catName) {
  const lower = (catName || '').toLowerCase().trim()
  for (const g of CAT_GROUPS) {
    if (g.match.some(m => lower.includes(m) || m.includes(lower))) return g.key
  }
  return 'other'
}

// ── Category → banner ─────────────────────────────────────────────────────────
const CATEGORY_BANNER_BASE = {
  beer: 'banner-beer', 'beer (mainstream)': 'banner-beer', 'beer mainstream': 'banner-beer',
  'craft beer': 'banner-beer-craft', 'beer - craft': 'banner-beer-craft', 'beer — craft': 'banner-beer-craft',
  'non alcoholic beer': 'banner-beer-non-alcoholic', 'non-alcoholic beer': 'banner-beer-non-alcoholic',
  wine: 'banner-wine', champagne: 'banner-champagne-sparkling',
  'champagne & sparkling wine': 'banner-champagne-sparkling', 'champagne & sparkling': 'banner-champagne-sparkling',
  sparkling: 'banner-champagne-sparkling', 'sparkling wine': 'banner-champagne-sparkling',
  'non alcoholic wine': 'banner-wine-non-alcoholic', 'non-alcoholic wine': 'banner-wine-non-alcoholic',
  'port sherry': 'banner-port-sherry-muscadel', 'port sherry muscadel etc': 'banner-port-sherry-muscadel',
  'port / sherry / muscadel': 'banner-port-sherry-muscadel',
  port: 'banner-port-sherry-muscadel', sherry: 'banner-port-sherry-muscadel', muscadel: 'banner-port-sherry-muscadel',
  'box wine': 'banner-wine',
  gin: 'banner-gin', vodka: 'banner-vodka',
  whiskey: 'banner-whisky', whisky: 'banner-whisky',
  rum: 'banner-rum', tequila: 'banner-tequila', brandy: 'banner-brandy',
  liqueur: 'banner-liqueur', liqueurs: 'banner-liqueur',
  aperitif: 'banner-aperitifs', aperitifs: 'banner-aperitifs', aperitiff: 'banner-aperitifs', aperitiffs: 'banner-aperitifs',
  cane: 'banner-cane', 'cane spirit': 'banner-cane',
  shooters: 'banner-shooters', spirits: 'banner-spirits-other',
  'other spirits': 'banner-spirits-other', 'non alcoholic spirits': 'banner-spirits-other',
  cider: 'banner-ciders-rtds', 'ciders & rtds': 'banner-ciders-rtds', ciders: 'banner-ciders-rtds', rtds: 'banner-ciders-rtds',
  'non alcoholic rtd': 'banner-rtd-non-alcoholic', 'non-alcoholic rtd': 'banner-rtd-non-alcoholic',
  'rtd cocktail': 'banner-rtd-cocktails', 'rtd cocktails': 'banner-rtd-cocktails', 'r t d cocktails': 'banner-rtd-cocktails',
  minerals: 'banner-minerals', water: 'banner-water', 'mineral water': 'banner-water', 'mineral drinks': 'banner-minerals',
  juice: 'banner-juice', 'fruit juice': 'banner-juice',
  energy: 'banner-softdrinks', 'energy drinks': 'banner-softdrinks', 'energy drink': 'banner-softdrinks',
  cordials: 'banner-cordials', cordial: 'banner-cordials',
  'soft drinks': 'banner-softdrinks', softdrinks: 'banner-softdrinks',
  cigarettes: 'banner-cigarettes-tobacco', tobacco: 'banner-cigarettes-tobacco',
  glassware: 'banner-glassware', gifts: 'banner-gifts', snacks: 'banner-snacks',
}

function getCategoryBannerSrc(category) {
  if (!category) return `/images/banners/banner-hero-${Math.floor(Math.random() * 4)}.webp`
  const base = CATEGORY_BANNER_BASE[category.toLowerCase().trim()]
  if (!base) return `/images/banners/banner-hero-${Math.floor(Math.random() * 4)}.webp`
  return `/images/banners/${base}-${Math.floor(Math.random() * 4)}.webp`
}

function CategoryBanner({ categoryName }) {
  const srcRef = useRef(getCategoryBannerSrc(categoryName))
  const prevCat = useRef(categoryName)
  if (prevCat.current !== categoryName) {
    srcRef.current = getCategoryBannerSrc(categoryName)
    prevCat.current = categoryName
  }
  const [err, setErr] = useState(false)
  if (err) return null
  return (
    <div className="relative mx-4 mb-3 h-28 rounded-2xl overflow-hidden">
      <img src={srcRef.current} alt={categoryName}
        className="w-full h-full object-cover" onError={() => setErr(true)} />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
      <div className="absolute bottom-3 left-4">
        <p className="font-heading text-white text-lg leading-tight drop-shadow">{categoryName}</p>
      </div>
    </div>
  )
}

// ── Category Drawer (bottom sheet with grouped accordion) ─────────────────────
function CategoryDrawer({ categories, selectedCatName, onSelect, onClose }) {
  const [openGroups, setOpenGroups] = useState({})

  // Auto-open the group that contains the selected category on mount
  useEffect(() => {
    if (selectedCatName) {
      const key = getGroupKey(selectedCatName)
      setOpenGroups({ [key]: true })
    }
  }, [])

  // Build groups from live category list
  const grouped = {}
  for (const g of CAT_GROUPS) grouped[g.key] = []
  for (const cat of categories) {
    const key = getGroupKey(cat.name)
    grouped[key].push(cat)
  }

  const toggleGroup = (key) =>
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }))

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-surface-card rounded-t-3xl border-t border-surface-border max-h-[82vh] flex flex-col animate-slide-up">
        {/* Handle */}
        <div className="flex items-center justify-center pt-3 pb-1 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-surface-border" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0 border-b border-surface-border">
          <span className="font-heading text-white text-base">Browse Categories</span>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-surface-elevated active:bg-surface-border transition-colors">
            <X size={16} className="text-gray-400" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1 pb-8 overscroll-contain sidebar-scroll">
          {/* All categories button */}
          <button
            onClick={() => { onSelect(null); onClose() }}
            className={`w-full flex items-center gap-3 px-5 py-3.5 border-b border-surface-border active:bg-surface-elevated transition-colors ${
              !selectedCatName ? 'text-primary' : 'text-gray-300'
            }`}
          >
            <span className="text-xl">🛒</span>
            <span className="font-display font-semibold text-sm flex-1 text-left">All Products</span>
            {!selectedCatName && <span className="w-2 h-2 rounded-full bg-primary" />}
          </button>

          {/* Grouped sections */}
          {CAT_GROUPS.map(group => {
            const cats = grouped[group.key] || []
            if (cats.length === 0) return null
            const isOpen = !!openGroups[group.key]
            const hasActive = cats.some(c => c.name === selectedCatName)

            return (
              <div key={group.key} className="border-b border-surface-border">
                {/* Group header — tappable to expand/collapse */}
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-surface-elevated transition-colors"
                >
                  <span className="text-xl">{group.emoji}</span>
                  <span className={`font-display font-semibold text-sm flex-1 text-left ${hasActive ? 'text-primary' : 'text-gray-200'}`}>
                    {group.label}
                  </span>
                  <span className="text-xs font-display text-gray-500 mr-2">{cats.length}</span>
                  {isOpen
                    ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" />
                    : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
                </button>

                {/* Sub-categories */}
                {isOpen && (
                  <div className="pb-1">
                    {cats.map(cat => {
                      const isActive = cat.name === selectedCatName
                      return (
                        <button
                          key={cat.id}
                          onClick={() => { onSelect(cat.name); onClose() }}
                          className={`w-full flex items-center gap-3 pl-12 pr-5 py-2.5 active:bg-surface-elevated transition-colors ${
                            isActive ? 'bg-primary/10' : ''
                          }`}
                        >
                          <span className="text-base">{getCategoryEmoji({ category_name: cat.name })}</span>
                          <span className={`font-display text-sm flex-1 text-left ${isActive ? 'text-primary font-semibold' : 'text-gray-400'}`}>
                            {cat.name}
                          </span>
                          {isActive && <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}

function norm(p) {
  return {
    id: p.id,
    name: p.description || p.stock_code,
    price: p.inclprice ?? p.inclprice2 ?? 0,
    originalPrice: null,
    rating: p.average_rating ?? null,
    reviews: p.review_count ?? 0,
    inStock: true,
    badge: null,
    primary_image: p.primary_image || null,
    image: p.image || null,
    stock_code: p.stock_code,
    category_id: p.category_id,
    category_name: p.category_name || null,
    brand: p.brand || null,
    packsize: p.packsize ?? null,
  }
}

export default function Shop() {
  const navigate = useNavigate()
  const activeCategory    = useAppStore(s => s.activeCategory)
  const setCategory       = useAppStore(s => s.setCategory)
  const searchQuery       = useAppStore(s => s.searchQuery)
  const { isAuthenticated, user } = useAuthStore()

  // ── Filter state ──────────────────────────────────────────────
  const [selectedCatName, setSelectedCatName] = useState(activeCategory || null)
  const [sort, setSort]                        = useState('newest')
  const [showFilters, setShowFilters]          = useState(false)
  const [showCatDrawer, setShowCatDrawer]      = useState(false)
  const [uomFilter, setUomFilter]              = useState('')
  const [priceRange, setPriceRange]            = useState(null) // { min, max, label } or null = any
  const [brandLetter, setBrandLetter]          = useState('')   // '' = all, 'A'..'Z', '0' = #0-9
  const [page, setPage]                        = useState(1)

  // Price presets — matching customer frontend exactly
  const PRICE_PRESETS = [
    { label: 'Any price',        min: null,  max: null   },
    { label: 'Under R1 000',     min: 0,     max: 1000   },
    { label: 'R1 000 – R2 500',  min: 1000,  max: 2500   },
    { label: 'R2 500 – R5 000',  min: 2500,  max: 5000   },
    { label: 'R5 000 – R10 000', min: 5000,  max: 10000  },
    { label: 'Over R10 000',     min: 10000, max: 999999 },
  ]

  const BRAND_ALPHA = ['#0–9', ...'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')]

  // Sync from Home → Shop navigation
  useEffect(() => {
    if (activeCategory && !selectedCatName) setSelectedCatName(activeCategory)
  }, [activeCategory])

  // ── Categories ────────────────────────────────────────────────
  const { data: cd } = useQuery({
    queryKey: ['categories'],
    queryFn: categoryService.getAll,
    staleTime: 10 * 60 * 1000,
  })
  const categories = Array.isArray(cd) ? cd : (cd?.items ?? cd?.categories ?? [])

  // ── Products ──────────────────────────────────────────────────
  const activeUom   = UOM_FILTERS.find(u => u.value === uomFilter)
  const packsizeMin = activeUom && !activeUom.keg ? (
    activeUom.value === 'single' ? 1 : activeUom.value === '4pack' ? 4 :
    activeUom.value === '6pack'  ? 6 : activeUom.value === '12pack' ? 12 :
    activeUom.value === 'case'   ? 20 : undefined
  ) : undefined
  const packsizeMax = activeUom && !activeUom.keg ? (
    activeUom.value === 'single' ? 1 : activeUom.value === '4pack' ? 4 :
    activeUom.value === '6pack'  ? 6 : activeUom.value === '12pack' ? 12 :
    undefined
  ) : undefined
  const isKeg = activeUom?.keg ?? false

  const { data: pd, isLoading } = useQuery({
    queryKey: ['products', 'shop', { selectedCatName, sort, priceRange, brandLetter, searchQuery, page, uomFilter }],
    queryFn: () => productService.getAll({
      category:     selectedCatName || undefined,
      sort,
      price_min:    priceRange?.min ?? undefined,
      price_max:    priceRange?.max ?? undefined,
      search:       searchQuery || undefined,
      limit:        PAGE_SIZE,
      page,
      packsize_min: packsizeMin,
      packsize_max: packsizeMax,
      keg:          isKeg || undefined,
      brand:        brandLetter || undefined,
    }),
    staleTime: 2 * 60 * 1000,
    keepPreviousData: true,
  })

  const raw = Array.isArray(pd) ? pd : (pd?.items ?? pd?.products ?? [])
  const allProducts = raw.map(norm)
  // Client-side UOM filter only for non-keg (keg is handled server-side)
  const products = (uomFilter && !isKeg)
    ? allProducts.filter(p => {
        const uom = UOM_FILTERS.find(u => u.value === uomFilter)
        return uom ? uom.test(p.packsize) : true
      })
    : allProducts
  const total      = pd?.total ?? allProducts.length
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const selectCategory = (catName) => {
    setSelectedCatName(catName)
    setCategory(catName)
    setPage(1)
  }

  const toggleUom = (val) => { setUomFilter(f => f === val ? '' : val); setPage(1) }
  const applySort = (val) => { setSort(val); setPage(1) }
  const clearAllFilters = () => { setPriceRange(null); setBrandLetter(''); setUomFilter(''); setPage(1) }

  return (
    <div className="page-container bg-surface-dark">
      <TopHeader title="Shop" />

      {/* Category banner */}
      {selectedCatName && <CategoryBanner key={selectedCatName} categoryName={selectedCatName} />}

      {/* Category button row + sort */}
      <div className="flex items-center gap-2 px-4 pt-3 pb-2">
        {/* Category picker button */}
        <button
          onClick={() => setShowCatDrawer(true)}
          className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs font-display font-semibold transition-colors flex-shrink-0 ${
            selectedCatName
              ? 'bg-primary/15 border-primary/40 text-primary'
              : 'bg-surface-card border-surface-border text-gray-400'
          }`}
        >
          <LayoutGrid size={13} />
          {selectedCatName
            ? <span className="max-w-[100px] truncate">{getCategoryEmoji({ category_name: selectedCatName })} {selectedCatName}</span>
            : 'Categories'}
          <ChevronDown size={11} className="flex-shrink-0" />
        </button>

        {/* Sort pills */}
        <div className="flex gap-2 overflow-x-auto scroll-hide flex-1">
          {SORT_OPTIONS.map(opt => (
            <button key={opt.value}
              onClick={() => applySort(opt.value)}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-display transition-colors ${
                sort === opt.value
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'bg-surface-card text-gray-400 border border-surface-border'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>

        {/* Filters button */}
        <button
          onClick={() => setShowFilters(v => !v)}
          className={`flex-shrink-0 p-2 rounded-xl border transition-colors ${
            priceRange || brandLetter
              ? 'bg-primary/20 border-primary/40 text-primary'
              : 'bg-surface-card border-surface-border text-gray-400'
          }`}>
          <SlidersHorizontal size={16} />
        </button>
      </div>

      {/* UOM filter pills — horizontally scrollable */}
      <div className="flex gap-2 px-4 pb-2 overflow-x-auto scroll-hide">
        {UOM_FILTERS.map(u => (
          <button
            key={u.value}
            onClick={() => toggleUom(u.value)}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-display font-medium border transition-colors ${
              uomFilter === u.value
                ? 'bg-primary border-primary text-white'
                : 'bg-surface-card border-surface-border text-gray-400 active:bg-surface-elevated'
            }`}
          >
            <span>{u.emoji}</span>
            <span>{u.label}</span>
          </button>
        ))}
      </div>

      {/* Filter panel — price presets + brand A–Z */}
      {showFilters && (
        <div className="mx-4 mb-3 p-4 bg-surface-card rounded-2xl border border-surface-border space-y-4">
          <div className="flex items-center justify-between">
            <span className="font-display text-sm text-white font-semibold">Filters</span>
            <div className="flex items-center gap-3">
              {(priceRange || brandLetter) && (
                <button onClick={clearAllFilters} className="text-xs text-primary font-display">
                  Clear all
                </button>
              )}
              <button onClick={() => setShowFilters(false)}>
                <X size={16} className="text-gray-400" />
              </button>
            </div>
          </div>

          {/* Price presets */}
          <div>
            <p className="text-xs font-display font-semibold text-gray-400 uppercase tracking-wide mb-2">Price Range</p>
            <div className="flex flex-wrap gap-1.5">
              {PRICE_PRESETS.map(r => {
                const isAny    = r.min === null && r.max === null
                const isActive = isAny ? !priceRange : (priceRange?.min === r.min && priceRange?.max === r.max)
                return (
                  <button
                    key={r.label}
                    onClick={() => { setPriceRange(isAny ? null : r); setPage(1) }}
                    className={`px-3 py-1.5 rounded-full text-xs font-display border transition-colors ${
                      isActive
                        ? 'bg-primary border-primary text-white'
                        : 'bg-surface-elevated border-surface-border text-gray-400 active:bg-surface-card'
                    }`}
                  >
                    {r.label}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Brand A–Z */}
          <div>
            <p className="text-xs font-display font-semibold text-gray-400 uppercase tracking-wide mb-2">Brand</p>
            <div className="flex flex-wrap gap-1.5">
              {BRAND_ALPHA.map(letter => {
                const val      = letter === '#0–9' ? '0' : letter
                const isActive = brandLetter === val
                return (
                  <button
                    key={letter}
                    onClick={() => { setBrandLetter(isActive ? '' : val); setPage(1) }}
                    className={`min-w-[28px] h-7 px-1.5 rounded-md text-xs font-display font-semibold border transition-colors ${
                      isActive
                        ? 'bg-primary border-primary text-white'
                        : 'bg-surface-elevated border-surface-border text-gray-400 active:bg-surface-card'
                    }`}
                    title={letter === '#0–9' ? 'Brands starting with 0–9' : `Brands starting with ${letter}`}
                  >
                    {letter}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {/* Active filters + count */}
      <div className="px-4 pb-2 flex items-center gap-2 flex-wrap">
        <span className="text-xs text-gray-500 font-display">
          {products.length} product{products.length !== 1 ? 's' : ''}
          {totalPages > 1 ? ` · page ${page} of ${totalPages}` : ''}
        </span>
        {uomFilter && (() => {
          const u = UOM_FILTERS.find(f => f.value === uomFilter)
          return u ? (
            <button onClick={() => toggleUom(uomFilter)}
              className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-display flex items-center gap-1">
              {u.emoji} {u.label} <X size={10} />
            </button>
          ) : null
        })()}
        {priceRange && (
          <button onClick={() => { setPriceRange(null); setPage(1) }}
            className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-display flex items-center gap-1">
            {priceRange.label} <X size={10} />
          </button>
        )}
        {brandLetter && (
          <button onClick={() => { setBrandLetter(''); setPage(1) }}
            className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full font-display flex items-center gap-1">
            Brand: {brandLetter === '0' ? '#0–9' : brandLetter} <X size={10} />
          </button>
        )}
        {selectedCatName && (
          <button onClick={() => selectCategory(null)}
            className="text-xs bg-surface-elevated text-gray-400 px-2 py-0.5 rounded-full font-display flex items-center gap-1">
            {selectedCatName} <X size={10} />
          </button>
        )}
      </div>

      {/* Product grid */}
      <div className="px-4 grid grid-cols-2 gap-3 pb-4">
        {isLoading
          ? Array(6).fill(0).map((_, i) => <ProductCardSkeleton key={i} />)
          : products.length === 0
            ? (
              <div className="col-span-2 text-center py-12">
                <p className="text-gray-400 font-display">No products found</p>
                {(selectedCatName || priceRange || brandLetter || uomFilter) && (
                  <button
                    onClick={() => { selectCategory(null); clearAllFilters() }}
                    className="mt-3 btn-outline py-2 px-4 text-sm">
                    Clear filters
                  </button>
                )}
              </div>
            )
            : products.map(p => (
              <ProductCard key={p.id} product={p} onClick={() => navigate(`/product/${p.id}`)} />
            ))
        }
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 pb-8">
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              disabled={page <= 1}
              className="w-9 h-9 rounded-xl bg-surface-card border border-surface-border flex items-center justify-center text-gray-400 disabled:opacity-30 active:bg-surface-elevated transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6"/>
              </svg>
            </button>

            {(() => {
              const delta = 1
              const left  = Math.max(1, page - delta)
              const right = Math.min(totalPages, page + delta)
              const pages = []
              if (left > 1) { pages.push(1); if (left > 2) pages.push('…') }
              for (let i = left; i <= right; i++) pages.push(i)
              if (right < totalPages) { if (right < totalPages - 1) pages.push('…'); pages.push(totalPages) }
              return pages.map((p, idx) =>
                p === '…' ? (
                  <span key={`ellipsis-${idx}`} className="text-gray-500 font-display text-sm px-1">…</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                    className={`w-9 h-9 rounded-xl font-display text-sm font-semibold transition-colors ${
                      p === page
                        ? 'bg-primary text-white'
                        : 'bg-surface-card border border-surface-border text-gray-400 active:bg-surface-elevated'
                    }`}>
                    {p}
                  </button>
                )
              )
            })()}

            <button
              onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
              disabled={page >= totalPages}
              className="w-9 h-9 rounded-xl bg-surface-card border border-surface-border flex items-center justify-center text-gray-400 disabled:opacity-30 active:bg-surface-elevated transition-colors">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </button>
          </div>

          {totalPages > 10 && (
            <p className="text-center text-gray-600 text-xs font-display mt-3">
              Page {page} of {totalPages}
            </p>
          )}
        </div>
      )}

      {/* Category drawer */}
      {showCatDrawer && (
        <CategoryDrawer
          categories={categories}
          selectedCatName={selectedCatName}
          onSelect={selectCategory}
          onClose={() => setShowCatDrawer(false)}
        />
      )}
    </div>
  )
}
